import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + per-day split
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam } from '../../utils/paymentMode';
import { formatDateIST } from '../../utils/dateFormat';

const QpayCenterDailyReport = () => {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();
  const [userId, setUserId] = useState('');

  // Dialog / Modal States
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  // Dropdown Lists
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);

  // Filter & Date States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all'); // Constituency
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
  }, []);

  const [rawTaluksList, setRawTaluksList] = useState([]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const distRes = await axios.get(`${API_BASE_URL}/districts`);
        if (distRes.status === 200 && distRes.data) {
          const list = Array.isArray(distRes.data)
            ? distRes.data
            : distRes.data.districts || [];
          setAllDistrictsList(list);
        }
      } catch (err) {
        console.error('Error fetching districts dropdown:', err);
      }

      try {
        const talukRes = await axios.get(`${API_BASE_URL}/taluks`);
        if (talukRes.status === 200 && talukRes.data) {
          const list = Array.isArray(talukRes.data)
            ? talukRes.data
            : talukRes.data.taluks || [];
          setRawTaluksList(list);
          const names = [...new Set(list.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
          setAllConstituenciesList(names.sort());
        }
      } catch (err) {
        console.error('Error fetching constituencies dropdown:', err);
      }
    };

    fetchDropdownData();
  }, []);

  // Update Constituency dropdown whenever District filter changes
  useEffect(() => {
    setVillageFilter('all');
    if (districtFilter === 'all') {
      const names = [...new Set(rawTaluksList.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    } else {
      const selectedDistObj = allDistrictsList.find(
        (d) => (d.districtName || d.name || '').toLowerCase() === districtFilter.toLowerCase()
      );
      const selectedDistId = selectedDistObj ? String(selectedDistObj._id || selectedDistObj.id) : null;

      const filteredTaluks = rawTaluksList.filter((t) => {
        const tDistId = String(t.district || t.districtId || '');
        const tDistName = (t.districtName || '').toLowerCase();
        return (
          (selectedDistId && tDistId === selectedDistId) ||
          tDistName === districtFilter.toLowerCase()
        );
      });

      const names = [...new Set(filteredTaluks.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    }
  }, [districtFilter, allDistrictsList, rawTaluksList]);

  // Fetch Daily Report Data
  const fetchDailyReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        userId,
        startDate,
        endDate,
        district: districtFilter,
        village: villageFilter,
        pincode: pincodeFilter,
        search: searchQuery,
        sortOrder,
        paymentMode: paymentModeParam(paymentMode), // [CASH-MODE]
      };

      const response = await axios.post(
        `${API_BASE_URL}/qpay-centers/daily-report`,
        payload
      );

      if (response.data && response.data.status === 'SUCCESS' && Array.isArray(response.data.data)) {
        setReportData(response.data.data);
        setTotalsByMode(response.data.totalsByMode || null); // [CASH-MODE]
        const dataCons = [...new Set(response.data.data.flatMap((item) => item.villages || []).filter((v) => v !== 'N/A'))];
        if (dataCons.length > 0) {
          setAllConstituenciesList((prev) => [...new Set([...prev, ...dataCons])].sort());
        }
      } else {
        setReportData([]);
        setTotalsByMode(null); // [CASH-MODE]
      }
    } catch (error) {
      console.error('Error fetching Q-Pay Center Daily Report:', error);
      setReportData([]);
      setTotalsByMode(null); // [CASH-MODE]
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, endDate, districtFilter, villageFilter, pincodeFilter, searchQuery, sortOrder, paymentMode]); // [CASH-MODE] paymentMode

  // [CASH-MODE] per-day UPI / Cash fields present only on the updated API
  const hasModeSplit = reportData.some((item) => item.upiAmount !== undefined || item.cashAmount !== undefined);
  const modeTotals = totalsByMode || (hasModeSplit
    ? reportData.reduce(
        (acc, item) => {
          acc.UPI.amount += Number(item.upiAmount) || 0;
          acc.CASH.amount += Number(item.cashAmount) || 0;
          return acc;
        },
        { UPI: { amount: 0 }, CASH: { amount: 0 } }
      )
    : null);
  const modeAmountKey = modeTotals?.UPI?.dailyTransaction !== undefined
    ? 'dailyTransaction'
    : (modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount');

  useEffect(() => {
    fetchDailyReport();
  }, [fetchDailyReport]);

  const handleOpenViewModal = (row) => {
    setSelectedRecord(row);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedRecord(null);
  };

  const handleToggleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // CSV Export Function (SuperAdmin Only)
  const handleExportCSV = () => {
    if (!can('qpayCentre.daily.export')) return;
    if (reportData.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = [
      'SL No',
      'Date',
      'Q-Pay Center ID',
      'State Head ID',
      'First Name',
      'Last Name',
      'List of DISTRICT',
      'List of CONSTITUENCY',
      'List of PINCODEs',
      'Daily Transaction',
      'Daily Commission',
      // [CASH-MODE]
      ...(hasModeSplit ? ['UPI Transaction', 'Cash Transaction', 'UPI Commission', 'Cash Commission'] : []),
    ];

    const rows = reportData.map((item) => [
      item.slNo,
      `"${item.date ? formatDateIST(item.date) : ''}"`,
      `"${item.customId || ''}"`,
      `"${item.stateHeadId || ''}"`,
      `"${item.firstName || ''}"`,
      `"${item.lastName || ''}"`,
      `"${(item.districts || []).join(', ')}"`,
      `"${(item.villages || []).join(', ')}"`,
      `"${(item.pincodes || []).join(', ')}"`,
      item.dailyTransaction || 0,
      item.dailyCommission || 0,
      // [CASH-MODE]
      ...(hasModeSplit ? [item.upiAmount || 0, item.cashAmount || 0, item.upiCommission || 0, item.cashCommission || 0] : []),
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `QPay_Center_Daily_Report_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'SL No',
      width: 70,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'date',
      headerName: 'Date',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value ? formatDateIST(params.value) : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'customId',
      headerName: 'Q-Pay Center ID',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateHeadId',
      headerName: 'State Head ID',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateStaffId',
      headerName: 'State Staff ID',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'qpayAgentId',
      headerName: 'Q-Pay Agent ID',
      minWidth: 170,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'pincode',
      headerName: 'Pincode',
      minWidth: 110,
      flex: 0.6,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'dailyTransaction',
      headerName: 'Daily Transaction (under this Q-Pay Center)',
      minWidth: 260,
      flex: 1.5,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#15803d' }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'dailyCommission',
      headerName: 'Daily Commission (under this Q-Pay Center)',
      minWidth: 260,
      flex: 1.5,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309' }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    // [CASH-MODE] per-day UPI / Cash columns
    ...(hasModeSplit
      ? [
          { field: 'upiAmount', headerName: 'UPI Transaction', minWidth: 140, flex: 1, headerAlign: 'right', align: 'right', renderCell: (params) => formatCurrency(params.value) },
          { field: 'cashAmount', headerName: 'Cash Transaction', minWidth: 140, flex: 1, headerAlign: 'right', align: 'right', renderCell: (params) => formatCurrency(params.value) },
          { field: 'upiCommission', headerName: 'UPI Commission', minWidth: 140, flex: 1, headerAlign: 'right', align: 'right', renderCell: (params) => formatCurrency(params.value) },
          { field: 'cashCommission', headerName: 'Cash Commission', minWidth: 140, flex: 1, headerAlign: 'right', align: 'right', renderCell: (params) => formatCurrency(params.value) },
        ]
      : []),
    {
      field: 'action',
      headerName: 'Action Button',
      minWidth: 120,
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Tooltip title="View Daily Report Details">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenViewModal(params.row)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <BackButton />
      <br />
      <Paper elevation={3} sx={{ p: 3, pr: 4, borderRadius: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            Q-PAY Centre Daily Report
          </Typography>

          <Box display="flex" gap={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SortIcon />}
              onClick={handleToggleSort}
              sx={{ height: 36, fontWeight: 700, borderRadius: 2 }}
            >
              Sort: Date ({sortOrder === 'desc' ? 'Newest' : 'Oldest'})
            </Button>

            {can('qpayCentre.daily.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ height: 36, fontWeight: 700, borderRadius: 2 }}
              >
                Export CSV
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Filter Section */}
        <Grid container spacing={2} alignItems="center">
          {/* Start Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              helperText={startDate ? `Showing: ${formatDateIST(startDate)}` : 'DD/MM/YYYY'}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              helperText={endDate ? `Showing: ${formatDateIST(endDate)}` : 'DD/MM/YYYY'}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          {/* District wise */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="district-label">District wise</InputLabel>
              <Select
                labelId="district-label"
                value={districtFilter}
                label="District wise"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {allDistrictsList.map((dist) => {
                  const name = typeof dist === 'string' ? dist : dist.districtName || dist.name;
                  return (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency wise */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="constituency-label">Constituency wise</InputLabel>
              <Select
                labelId="constituency-label"
                value={villageFilter}
                label="Constituency wise"
                onChange={(e) => setVillageFilter(e.target.value)}
              >
                <MenuItem value="all">All Constituencies</MenuItem>
                {allConstituenciesList.map((cons) => (
                  <MenuItem key={cons} value={cons}>
                    {cons}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Pincode wise */}
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Pincode wise"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
              placeholder="e.g. 695000"
            />
          </Grid>

          {/* Search Input */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Search Q-Pay Center / Mobile / Email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          {/* [CASH-MODE] */}
          <Grid item xs={12} sm={6} md={2}>
            <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
          </Grid>
        </Grid>
        {/* [CASH-MODE] */}
        <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mt: 2 }} />
      </Paper>

      {/* Data Table */}
      <Paper elevation={3} sx={{ height: 600, width: '100%', borderRadius: 2 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading Q-PAY Centre Daily Report...
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={reportData}
            columns={columns}
            getRowId={(row) => `${row.customId}-${row.date}-${row.slNo}`}
            rowHeight={60}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#1e293b',
                fontSize: 14,
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f1f5f9',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              },
            }}
          />
        )}
      </Paper>

      {/* View Detail Dialog Modal */}
      <Dialog
        open={openViewModal}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            backgroundColor: '#1976d2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Daily Report Summary
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedRecord && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Report Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {formatDateIST(selectedRecord.date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Q-Pay Center ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {selectedRecord.customId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  State Head ID
                </Typography>
                <Typography variant="body1">{selectedRecord.stateHeadId}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  First Name / Last Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.firstName} {selectedRecord.lastName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1">{selectedRecord.mobile}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Email ID
                </Typography>
                <Typography variant="body1">{selectedRecord.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Daily Transaction Total
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#15803d' }}>
                  {formatCurrency(selectedRecord.dailyTransaction)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Daily Commission Total
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#b45309' }}>
                  {formatCurrency(selectedRecord.dailyCommission)}
                </Typography>
              </Grid>
              {/* [CASH-MODE] */}
              {(selectedRecord.upiAmount !== undefined || selectedRecord.cashAmount !== undefined) && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">UPI Transaction / Commission</Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedRecord.upiAmount)} / {formatCurrency(selectedRecord.upiCommission)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Cash Transaction / Commission</Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedRecord.cashAmount)} / {formatCurrency(selectedRecord.cashCommission)}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of DISTRICT
                </Typography>
                <Typography variant="body1">
                  {(selectedRecord.districts || []).join(', ')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of VILLAGE
                </Typography>
                <Typography variant="body1">
                  {(selectedRecord.villages || []).join(', ')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  List of PINCODE’s
                </Typography>
                <Typography variant="body1">
                  {(selectedRecord.pincodes || []).join(', ')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewModal} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QpayCenterDailyReport;
